/**
 * Sélecteur d'équipe dans l'en-tête de `/equipes/:teamId` (217:4531, décisions 2026-09-27) —
 * même grammaire que `OrganizationSwitcher` : titre suivi d'un chevron, menu groupé par
 * organisation (`MenuGroupe`, titre ink-faint), rôle d'équipe en `Tag`, coche carrée sur
 * l'équipe courante, puis « Voir toutes les équipes » et « Nouvelle équipe ».
 *
 * Une seule équipe au total : pas de chevron, pas de menu.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Menu,
  MenuChoixUnique,
  MenuGroupe,
  MenuItem,
  MenuSeparateur,
  Tag,
} from '@exidis/ui/composants';
import { api } from '../lib/api.js';
import { fetchAllPages } from '../lib/pagination.js';
import { useAsync } from '../hooks/useAsync.js';
import { TEAM_ROLE_KEYS } from '../lib/roleLabels.js';
import { CreateTeamDialog } from '../dialogs/CreateTeamDialog.jsx';
import { PAGE_TITLE_TRIGGER } from '../ui/theme.js';

/**
 * Regroupe les équipes par organisation, dans l'ordre de première apparition — sauf l'espace
 * personnel, toujours en premier et titré « Mon espace », jamais du nom de l'utilisateur
 * (ORG-8, même règle que `TeamsPage::groupByOrganization`).
 */
function groupByOrganization(teams) {
  const order = [];
  const byOrg = new Map();
  for (const team of teams) {
    if (!byOrg.has(team.organizationId)) {
      order.push(team.organizationId);
      byOrg.set(team.organizationId, {
        organizationId: team.organizationId,
        organizationName: team.organizationName,
        personal: team.organizationKind === 'personnel',
        teams: [],
      });
    }
    byOrg.get(team.organizationId).teams.push(team);
  }
  return order.map((id) => byOrg.get(id)).sort((a, b) => Number(b.personal) - Number(a.personal));
}

export function TeamSwitcher({ teamId, name }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);

  const { data, reload } = useAsync((signal) => fetchAllPages('/teams', { signal }), []);
  const teams = data?.data ?? [];
  // La modale reçoit les adhésions réelles (statut, nature) et non les groupes du menu : elle
  // n'offre que les organisations actives, et l'espace personnel une seule fois (I2, I3).
  const { data: organizationsData } = useAsync(
    (signal) => api.get('/me/organizations', { signal }),
    [],
  );
  const organizations = organizationsData?.data ?? [];
  const groups = groupByOrganization(teams);
  const currentTeam = teams.find((team) => team.id === teamId);

  if (teams.length <= 1) {
    return name;
  }

  return (
    <>
      <Menu
        libelle={t('teams.switcherMenuAria')}
        // Voir `OrganizationSwitcher` : même correctif, même raison (titre à gauche de page,
        // panneau `right-0` par défaut qui déborde hors fenêtre côté gauche).
        style={{ left: 0, right: 'auto' }}
        declencheur={(attrs) => (
          <button type="button" className={PAGE_TITLE_TRIGGER} {...attrs}>
            {name}
            <ChevronSelecteur />
          </button>
        )}
      >
        {groups.map((group) => (
          <MenuGroupe
            key={group.organizationId}
            titre={group.personal ? t('space.personal') : group.organizationName}
          >
            {group.teams.map((team) => (
              <MenuChoixUnique
                key={team.id}
                choisi={team.id === teamId}
                onSelect={() => navigate(`/equipes/${team.id}`)}
              >
                <span className="flex-1 truncate text-left">{team.name}</span>
                <Tag>{t(TEAM_ROLE_KEYS[team.role] ?? team.role)}</Tag>
              </MenuChoixUnique>
            ))}
          </MenuGroupe>
        ))}
        <MenuSeparateur />
        <MenuItem onSelect={() => navigate('/equipes')}>{t('teams.seeAll')}</MenuItem>
        <MenuItem onSelect={() => setCreating(true)}>{t('teams.newTeam')}</MenuItem>
      </Menu>

      {creating && (
        <CreateTeamDialog
          organizations={organizations}
          currentOrganizationId={currentTeam?.organizationId}
          onClose={() => setCreating(false)}
          onCreated={(created) => {
            setCreating(false);
            reload();
            navigate(`/equipes/${created.id}`);
          }}
        />
      )}
    </>
  );
}

/** Même grammaire que `OrganizationSwitcher::ChevronSelecteur` — voir son commentaire. */
function ChevronSelecteur() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="square"
      className="h-3.5 w-3.5 shrink-0"
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}
